// ──────────────────────────────────────────────
// UploadService — chunk upload queue with retry, concurrency control,
// integrity verification, and dead letter handling.
//
// See ADR-005 for design rationale.
//
// Usage:
//   const upload = new UploadService(config);
//   upload.start();
//   upload.enqueue({ sessionId, chunkIndex, chunkId, contentType, byteSize });
//   // ... chunks upload automatically with retry and concurrency control
//   upload.dispose();
// ──────────────────────────────────────────────

import SentryService from '@services/SentryService';

import type { StorageService } from '../storage';

import { BaseService } from './BaseService';
import { NetworkMonitor } from './NetworkMonitor';
import type {
  ChunkUploadRequest,
  DeadLetterEntry,
  DrainResult,
  PresignedUrlResponse,
  QueueItem,
  UploadEventMap,
  UploadServiceConfig,
  UploadServiceStatus,
  UploadState,
} from './types';

// ── Constants ──

const DEFAULT_MAX_CONCURRENCY = 3;
const DEFAULT_MAX_RETRIES = 5;
const DEFAULT_RETRY_BASE_MS = 1000;
const DEFAULT_RETRY_CAP_MS = 30000;
const DEFAULT_HEALTH_CHECK_INTERVAL_MS = 30000;
const DEFAULT_MULTIPART_THRESHOLD_BYTES = 5 * 1024 * 1024; // 5MB
const DEFAULT_UPLOAD_TIMEOUT_MS = 45000;
const DEAD_LETTER_SESSION_KEY = '__dead_letter__';

const DRAIN_MAX_RETRIES = 2;
const DEFAULT_DRAIN_TIMEOUT_MS = 30_000;

function initialUploadState(): UploadState {
  return {
    status: 'idle',
    queueDepth: 0,
    inFlightCount: 0,
    completedCount: 0,
    failedCount: 0,
    bytesUploaded: 0,
    totalBytes: 0,
    isOnline: true,
    isDraining: false,
    deadLetterQueue: [],
  };
}

/**
 * UploadService — owns the chunk upload queue, retry logic, concurrency
 * control, integrity verification, and dead letter handling.
 *
 * Consumes StorageService to read chunk data and delete on success.
 * Framework-agnostic: React connects via useSyncExternalStore.
 */
export class UploadService extends BaseService<UploadState, UploadEventMap> {
  private readonly storageService: StorageService;
  private readonly apiBaseUrl: string;
  private readonly getAccessToken: () => Promise<string>;
  private readonly maxConcurrency: number;
  private readonly maxRetries: number;
  private readonly retryBaseMs: number;
  private readonly retryCapMs: number;
  private readonly multipartThresholdBytes: number;
  private readonly uploadTimeoutMs: number;

  private readonly networkMonitor: NetworkMonitor;
  private readonly queue: QueueItem[] = [];
  private readonly inFlight = new Map<string, QueueItem>();
  private disposed = false;
  private started = false;

  // Drain mode state
  private drainResolve: ((result: DrainResult) => void) | null = null;
  private drainTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private drainCompletedAtStart = 0;
  private drainFailedAtStart = 0;

  constructor(config: UploadServiceConfig) {
    super(initialUploadState());

    this.storageService = config.storageService;
    this.apiBaseUrl = config.apiBaseUrl;
    this.getAccessToken = config.getAccessToken;
    this.maxConcurrency = config.maxConcurrency ?? DEFAULT_MAX_CONCURRENCY;
    this.maxRetries = config.maxRetries ?? DEFAULT_MAX_RETRIES;
    this.retryBaseMs = config.retryBaseMs ?? DEFAULT_RETRY_BASE_MS;
    this.retryCapMs = config.retryCapMs ?? DEFAULT_RETRY_CAP_MS;
    this.multipartThresholdBytes = config.multipartThresholdBytes ?? DEFAULT_MULTIPART_THRESHOLD_BYTES;
    this.uploadTimeoutMs = config.uploadTimeoutMs ?? DEFAULT_UPLOAD_TIMEOUT_MS;

    const healthCheckUrl = config.healthCheckUrl ?? `${config.apiBaseUrl}/health`;
    const healthCheckIntervalMs = config.healthCheckIntervalMs ?? DEFAULT_HEALTH_CHECK_INTERVAL_MS;

    this.networkMonitor = new NetworkMonitor({ healthCheckUrl, healthCheckIntervalMs });
  }

  // ── Lifecycle ──

  /** Start listening for network changes. Call once after construction. */
  start(): void {
    if (this.started || this.disposed) return;
    this.started = true;

    this.networkMonitor.onChange((isOnline) => {
      this.updateState({ isOnline });
      this.emit('networkChanged', { isOnline });

      if (isOnline) {
        this.setServiceStatus('uploading');
        this.retryAllDeadLetters();
        this.processQueue();
      } else {
        this.setServiceStatus('paused');
      }
    });

    this.networkMonitor.start();
    this.updateState({ isOnline: this.networkMonitor.isOnline() });

    // Load persisted dead letter entries
    void this.loadDeadLetterQueue();

    SentryService.addBreadcrumb({
      category: 'upload.service',
      message: 'UploadService started',
      level: 'info',
    });
  }

  override dispose(): void {
    if (this.disposed) return;
    this.disposed = true;

    // Resolve any pending drain before disposing
    if (this.drainResolve) {
      this.resolveDrain(false);
    }

    // Abort all in-flight uploads
    for (const item of this.inFlight.values()) {
      item.abortController.abort();
    }
    this.inFlight.clear();
    this.queue.length = 0;

    this.networkMonitor.dispose();

    this.setState(initialUploadState());

    SentryService.addBreadcrumb({
      category: 'upload.service',
      message: 'UploadService disposed',
      level: 'info',
    });

    super.dispose();
  }

  // ── Public API ──

  /** Add a chunk to the upload queue. */
  enqueue(request: ChunkUploadRequest): void {
    if (this.disposed) return;

    const item: QueueItem = {
      request,
      attempt: 0,
      addedAt: Date.now(),
      abortController: new AbortController(),
    };

    this.queue.push(item);
    this.updateState({
      queueDepth: this.queue.length,
      totalBytes: this.state.totalBytes + request.byteSize,
    });

    SentryService.addBreadcrumb({
      category: 'upload.service',
      message: `Enqueued chunk ${request.chunkId} (position ${request.chunkIndex})`,
      level: 'info',
      data: { sessionId: request.sessionId, byteSize: request.byteSize },
    });

    // Start heartbeat when queue is active
    this.networkMonitor.startHeartbeat();

    if (this.networkMonitor.isOnline() && this.state.status !== 'paused') {
      if (this.state.status === 'idle') {
        this.setServiceStatus('uploading');
      }
      this.processQueue();
    }
  }

  /** Abort a specific in-flight upload and remove from queue. */
  abort(chunkId: string): void {
    // Abort if in-flight
    const inFlightItem = this.inFlight.get(chunkId);
    if (inFlightItem) {
      inFlightItem.abortController.abort();
      // uploadOne will handle cleanup via catch
    }

    // Remove from queue if still queued
    const queueIdx = this.queue.findIndex(item => item.request.chunkId === chunkId);
    if (queueIdx !== -1) {
      this.queue.splice(queueIdx, 1);
      this.updateState({ queueDepth: this.queue.length });
    }
  }

  /** Abort all in-flight uploads and clear the queue. */
  abortAll(): void {
    for (const item of this.inFlight.values()) {
      item.abortController.abort();
    }

    this.queue.length = 0;
    // inFlight cleanup happens in uploadOne catch blocks

    this.updateState({
      queueDepth: 0,
      status: 'idle',
    });

    this.networkMonitor.stopHeartbeat();
  }

  /** Retry a dead-lettered chunk. Removes it from dead letter queue and re-enqueues. */
  async retryDeadLetter(sessionId: string, chunkIndex: number): Promise<void> {
    const idx = this.state.deadLetterQueue.findIndex(
      e => e.sessionId === sessionId && e.chunkIndex === chunkIndex,
    );
    if (idx === -1) return;

    const entry = this.state.deadLetterQueue[idx];

    // Remove from dead letter queue
    const updated = [...this.state.deadLetterQueue];
    updated.splice(idx, 1);
    this.updateState({
      deadLetterQueue: updated,
      failedCount: updated.length,
    });

    // Persist updated dead letter queue
    await this.persistDeadLetterQueue(updated);

    // Re-enqueue without incrementing totalBytes (already counted on first enqueue)
    const item: QueueItem = {
      request: {
        sessionId: entry.sessionId,
        chunkIndex: entry.chunkIndex,
        chunkId: entry.chunkId,
        contentType: entry.contentType,
        byteSize: entry.byteSize,
      },
      attempt: 0,
      addedAt: Date.now(),
      abortController: new AbortController(),
    };
    this.queue.push(item);
    this.updateState({ queueDepth: this.queue.length });
    this.networkMonitor.startHeartbeat();
    this.processQueue();
  }

  /** Re-enqueue all dead-lettered chunks. Called on network restore. */
  private retryAllDeadLetters(): void {
    const entries = [...this.state.deadLetterQueue];
    if (entries.length === 0) return;

    for (const entry of entries) {
      void this.retryDeadLetter(entry.sessionId, entry.chunkIndex);
    }
  }

  /**
   * Enter drain mode: wait for all queued/in-flight uploads to complete or fail.
   *
   * During drain mode, retries are reduced (2 attempts instead of 5) for faster
   * failure. If the timeout fires, all remaining items are force-dead-lettered.
   *
   * Returns a DrainResult summarizing what happened.
   */
  async drain(timeoutMs: number = DEFAULT_DRAIN_TIMEOUT_MS): Promise<DrainResult> {
    // If already empty, resolve immediately
    if (this.queue.length === 0 && this.inFlight.size === 0) {
      return {
        completedCount: 0,
        failedCount: 0,
        timedOut: false,
      };
    }

    this.updateState({ isDraining: true });
    this.drainCompletedAtStart = this.state.completedCount;
    this.drainFailedAtStart = this.state.failedCount;

    SentryService.addBreadcrumb({
      category: 'upload.service',
      message: `Entering drain mode (timeout=${timeoutMs}ms, queued=${this.queue.length}, inFlight=${this.inFlight.size})`,
      level: 'info',
    });

    return new Promise<DrainResult>((resolve) => {
      this.drainResolve = resolve;

      this.drainTimeoutId = setTimeout(() => {
        this.forceDrainAll();
      }, timeoutMs);

      // If not currently processing, kick off
      if (this.networkMonitor.isOnline()) {
        this.processQueue();
      }
    });
  }

  // ── Queue Processing ──

  private processQueue(): void {
    if (this.disposed) return;
    if (!this.networkMonitor.isOnline()) return;

    while (this.inFlight.size < this.maxConcurrency && this.queue.length > 0) {
      const item = this.queue.shift()!;
      this.updateState({ queueDepth: this.queue.length });

      // Give a fresh AbortController for retries
      if (item.abortController.signal.aborted) {
        item.abortController = new AbortController();
      }

      this.inFlight.set(item.request.chunkId, item);
      this.updateState({ inFlightCount: this.inFlight.size });

      void this.uploadOne(item);
    }

    // If queue and in-flight are both empty, we're done
    if (this.queue.length === 0 && this.inFlight.size === 0 && this.state.status === 'uploading') {
      this.setServiceStatus('idle');
      this.networkMonitor.stopHeartbeat();
      this.emit('queueDrained', { completedCount: this.state.completedCount });

      // Resolve drain promise if in drain mode
      this.resolveDrain(false);
    }
  }

  private async uploadOne(item: QueueItem): Promise<void> {
    const { request } = item;
    const span = SentryService.startInactiveSpan(
      'upload.chunk',
      `Upload chunk ${request.chunkId} (attempt ${item.attempt + 1})`,
    );

    try {
      span?.setAttribute('chunkId', request.chunkId);
      span?.setAttribute('chunkIndex', request.chunkIndex);
      span?.setAttribute('sessionId', request.sessionId);
      span?.setAttribute('attempt', item.attempt + 1);

      // 1. Read chunk data from StorageService
      const chunkData = await this.storageService.readChunk(request.sessionId, request.chunkIndex);
      if (!chunkData) {
        throw new UploadError(
          `Chunk at position ${request.chunkIndex} not found in StorageService`,
          'CHUNK_NOT_FOUND',
          false, // not retryable
        );
      }

      span?.setAttribute('chunkSize', chunkData.byteLength);

      // 2. Get access token
      const accessToken = await this.getAccessToken();

      // 3. Request presigned URL
      const presigned = await this.requestPresignedUrl(request, accessToken, item.abortController.signal);
      span?.setAttribute('s3ObjectId', presigned.s3_object_id);

      // 4. Upload to S3 (with proxy fallback for corporate firewalls)
      let etag: string | undefined;
      if (chunkData.byteLength > this.multipartThresholdBytes) {
        // Multipart upload for oversized chunks
        etag = await this.uploadMultipart(request, chunkData, accessToken, item.abortController.signal);
      } else {
        try {
          etag = await this.uploadSinglePut(presigned.upload_url, chunkData, request.contentType, item.abortController.signal);
        } catch (s3Error) {
          // If S3 PUT failed with a network-like error, try proxy fallback
          // (corporate firewalls may block S3 but allow API traffic)
          if (!item.abortController.signal.aborted) {
            SentryService.addBreadcrumb({
              category: 'upload.service',
              message: `S3 PUT failed, attempting proxy fallback for chunk ${request.chunkId}`,
              level: 'warning',
              data: { error: s3Error instanceof Error ? s3Error.message : String(s3Error) },
            });

            etag = await this.uploadViaProxy(
              presigned.upload_url, chunkData, request.contentType, accessToken, item.abortController.signal,
            );
            span?.setAttribute('uploadMethod', 'proxy');
          } else {
            throw s3Error;
          }
        }
      }

      span?.setAttribute('etag', etag ?? 'none');

      // 5. Confirm upload with backend
      await this.confirmChunkUpload(request, presigned.s3_object_id, accessToken, item.abortController.signal);

      // 6. Delete chunk from StorageService on success
      try {
        await this.storageService.deleteChunk(request.sessionId, request.chunkIndex);
      } catch (cleanupError) {
        // Don't fail the upload for cleanup errors
        SentryService.addBreadcrumb({
          category: 'upload.cleanup',
          message: `Failed to delete chunk ${request.chunkId} from storage: ${cleanupError}`,
          level: 'warning',
        });
      }

      // Success!
      this.inFlight.delete(request.chunkId);
      this.updateState({
        inFlightCount: this.inFlight.size,
        completedCount: this.state.completedCount + 1,
        bytesUploaded: this.state.bytesUploaded + (chunkData.byteLength),
      });

      this.emit('chunkUploaded', {
        sessionId: request.sessionId,
        chunkIndex: request.chunkIndex,
        s3ObjectId: presigned.s3_object_id,
        etag,
      });

      // If DLQ has entries and an upload just succeeded, the server is healthy — retry DLQ
      this.retryAllDeadLetters();

      span?.setStatus({ code: 1 });

      SentryService.addBreadcrumb({
        category: 'upload.service',
        message: `Successfully uploaded chunk ${request.chunkId}`,
        level: 'info',
      });

      // Process next items in queue
      this.processQueue();
    } catch (error) {
      span?.setStatus({ code: 2 });
      span?.setAttribute('error', error instanceof Error ? error.message : String(error));

      this.inFlight.delete(request.chunkId);
      this.updateState({ inFlightCount: this.inFlight.size });

      // Check if aborted (user-initiated or dispose)
      if (item.abortController.signal.aborted) {
        SentryService.addBreadcrumb({
          category: 'upload.service',
          message: `Upload aborted for chunk ${request.chunkId}`,
          level: 'info',
        });
        this.processQueue();
        return;
      }

      await this.handleUploadFailure(item, error);
    } finally {
      span?.end();
    }
  }

  // ── Upload Steps ──

  private async requestPresignedUrl(
    request: ChunkUploadRequest,
    accessToken: string,
    signal: AbortSignal,
  ): Promise<PresignedUrlResponse> {
    const chunkFileExtension = request.contentType.split('/')[1]?.split(';')[0] ?? 'webm';
    const fileName = `${request.sessionId}/chunk${request.chunkIndex}.${chunkFileExtension}`;

    const response = await fetch(`${this.apiBaseUrl}/transcriptionSession/createUploadURL`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        object_id: fileName,
        content_type: request.contentType.split(';')[0],
      }),
      signal,
    });

    if (!response.ok) {
      const status = response.status;
      throw new UploadError(
        `Failed to get presigned URL: HTTP ${status}`,
        'PRESIGN_FAILED',
        status === 429 || status >= 500,
      );
    }

    return (await response.json()) as PresignedUrlResponse;
  }

  private async uploadSinglePut(
    uploadUrl: string,
    data: ArrayBuffer,
    contentType: string,
    signal: AbortSignal,
  ): Promise<string | undefined> {
    const controller = new AbortController();

    // Compose signals: user abort + timeout
    const timeoutId = setTimeout(() => controller.abort(), this.uploadTimeoutMs);
    const onUserAbort = () => controller.abort();
    signal.addEventListener('abort', onUserAbort);

    try {
      const mimeType = contentType.split(';')[0];

      const headers: Record<string, string> = {
        'Content-Type': mimeType,
      };

      const response = await fetch(uploadUrl, {
        method: 'PUT',
        body: data,
        headers,
        signal: controller.signal,
      });

      if (!response.ok) {
        const status = response.status;
        throw new UploadError(
          `S3 PUT failed: HTTP ${status}`,
          'S3_PUT_FAILED',
          status === 429 || status >= 500,
        );
      }

      // Extract ETag for integrity verification
      const etag = response.headers.get('etag')?.replace(/"/g, '');
      return etag ?? undefined;
    } finally {
      clearTimeout(timeoutId);
      signal.removeEventListener('abort', onUserAbort);
    }
  }

  /**
   * Multipart upload for chunks exceeding the threshold (>5MB).
   *
   * Falls back to single PUT if the multipart endpoints are not available
   * on the backend yet (returns 404).
   */
  private async uploadMultipart(
    request: ChunkUploadRequest,
    data: ArrayBuffer,
    accessToken: string,
    signal: AbortSignal,
  ): Promise<string | undefined> {
    const partSize = this.multipartThresholdBytes;
    const chunkFileExtension = request.contentType.split('/')[1]?.split(';')[0] ?? 'webm';
    const fileName = `${request.sessionId}/chunk${request.chunkIndex}.${chunkFileExtension}`;
    const mimeType = request.contentType.split(';')[0];

    // Step 1: Initiate multipart upload
    let uploadId: string;
    let s3Key: string;
    try {
      const initResponse = await fetch(`${this.apiBaseUrl}/transcriptionSession/createMultipartUpload`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          object_id: fileName,
          content_type: mimeType,
        }),
        signal,
      });

      if (initResponse.status === 404) {
        // Backend doesn't support multipart yet — fall back to single PUT
        SentryService.addBreadcrumb({
          category: 'upload.service',
          message: 'Multipart endpoint not available, falling back to single PUT',
          level: 'warning',
          data: { chunkSize: data.byteLength },
        });

        // Get a presigned URL for single PUT
        const presigned = await this.requestPresignedUrl(request, accessToken, signal);
        return this.uploadSinglePut(presigned.upload_url, data, request.contentType, signal);
      }

      if (!initResponse.ok) {
        throw new UploadError(
          `Failed to initiate multipart upload: HTTP ${initResponse.status}`,
          'MULTIPART_INIT_FAILED',
          initResponse.status >= 500,
        );
      }

      const initResult = await initResponse.json();
      uploadId = initResult.upload_id;
      s3Key = initResult.s3_key;
    } catch (error) {
      if (error instanceof UploadError) throw error;
      throw new UploadError(
        `Multipart init failed: ${error instanceof Error ? error.message : String(error)}`,
        'MULTIPART_INIT_FAILED',
        true,
      );
    }

    // Step 2: Upload parts
    const totalParts = Math.ceil(data.byteLength / partSize);
    const completedParts: Array<{ PartNumber: number; ETag: string }> = [];

    for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
      if (signal.aborted) throw new UploadError('Upload aborted', 'ABORTED', false);

      const start = (partNumber - 1) * partSize;
      const end = Math.min(start + partSize, data.byteLength);
      const partData = data.slice(start, end);

      // Get presigned URL for this part
      const partUrlResponse = await fetch(`${this.apiBaseUrl}/transcriptionSession/createUploadPartURL`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          upload_id: uploadId,
          s3_key: s3Key,
          part_number: partNumber,
        }),
        signal,
      });

      if (!partUrlResponse.ok) {
        throw new UploadError(
          `Failed to get part URL: HTTP ${partUrlResponse.status}`,
          'MULTIPART_PART_URL_FAILED',
          partUrlResponse.status >= 500,
        );
      }

      const { upload_url: partUrl } = await partUrlResponse.json();

      // Upload the part
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.uploadTimeoutMs);
      const onUserAbort = () => controller.abort();
      signal.addEventListener('abort', onUserAbort);

      try {
        const partResponse = await fetch(partUrl, {
          method: 'PUT',
          body: partData,
          signal: controller.signal,
        });

        if (!partResponse.ok) {
          throw new UploadError(
            `Part ${partNumber} upload failed: HTTP ${partResponse.status}`,
            'MULTIPART_PART_FAILED',
            partResponse.status >= 500,
          );
        }

        const etag = partResponse.headers.get('etag')?.replace(/"/g, '');
        if (!etag) {
          throw new UploadError(
            `Part ${partNumber} upload returned no ETag`,
            'MULTIPART_PART_NO_ETAG',
            true,
          );
        }

        completedParts.push({ PartNumber: partNumber, ETag: etag });
      } finally {
        clearTimeout(timeoutId);
        signal.removeEventListener('abort', onUserAbort);
      }
    }

    // Step 3: Complete multipart upload
    const completeResponse = await fetch(`${this.apiBaseUrl}/transcriptionSession/completeMultipartUpload`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        upload_id: uploadId,
        s3_key: s3Key,
        parts: completedParts,
      }),
      signal,
    });

    if (!completeResponse.ok) {
      throw new UploadError(
        `Failed to complete multipart upload: HTTP ${completeResponse.status}`,
        'MULTIPART_COMPLETE_FAILED',
        completeResponse.status >= 500,
      );
    }

    const completeResult = await completeResponse.json();
    return completeResult.etag?.replace(/"/g, '') ?? undefined;
  }

  private async confirmChunkUpload(
    request: ChunkUploadRequest,
    s3ObjectId: string,
    accessToken: string,
    signal: AbortSignal,
  ): Promise<void> {
    const response = await fetch(`${this.apiBaseUrl}/transcriptionSession/addChunkToSession`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        chunk_id: request.chunkId,
        chunk_position: request.chunkIndex,
        transcription_session_id: request.sessionId,
        s3_object_id: s3ObjectId,
      }),
      signal,
    });

    if (!response.ok) {
      const status = response.status;
      throw new UploadError(
        `Failed to confirm chunk upload: HTTP ${status}`,
        'CONFIRM_FAILED',
        status === 429 || status >= 500,
      );
    }
  }

  /**
   * Proxy fallback: upload chunk data via the backend API instead of direct S3 PUT.
   * Used when corporate firewalls block S3 but allow API traffic.
   */
  private async uploadViaProxy(
    presignedUrl: string,
    data: ArrayBuffer,
    contentType: string,
    accessToken: string,
    signal: AbortSignal,
  ): Promise<string | undefined> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.uploadTimeoutMs);
    const onUserAbort = () => controller.abort();
    signal.addEventListener('abort', onUserAbort);

    try {
      const mimeType = contentType.split(';')[0];
      const blob = new Blob([data], { type: mimeType });

      const formData = new FormData();
      formData.append('audio', blob, 'chunk.webm');
      formData.append('presigned_url', presignedUrl);
      formData.append('content_type', mimeType);

      const response = await fetch(
        `${this.apiBaseUrl}/v2/provider/transcriptions/proxy-upload`,
        {
          method: 'POST',
          body: formData,
          headers: { Authorization: `Bearer ${accessToken}` },
          signal: controller.signal,
        },
      );

      if (!response.ok) {
        throw new UploadError(
          `Proxy upload failed: HTTP ${response.status}`,
          'PROXY_UPLOAD_FAILED',
          response.status === 429 || response.status >= 500,
        );
      }

      SentryService.addBreadcrumb({
        category: 'upload.service',
        message: 'Proxy fallback upload succeeded',
        level: 'info',
      });

      return undefined; // Proxy doesn't return ETag
    } finally {
      clearTimeout(timeoutId);
      signal.removeEventListener('abort', onUserAbort);
    }
  }

  // ── Retry & Dead Letter ──

  private async handleUploadFailure(item: QueueItem, error: unknown): Promise<void> {
    const isUploadError = error instanceof UploadError;
    const isRetryable = isUploadError ? error.retryable : true;
    const errorMessage = error instanceof Error ? error.message : String(error);
    const nextAttempt = item.attempt + 1;
    const effectiveMaxRetries = this.state.isDraining ? DRAIN_MAX_RETRIES : this.maxRetries;
    const willRetry = isRetryable && nextAttempt < effectiveMaxRetries;

    this.emit('chunkFailed', {
      sessionId: item.request.sessionId,
      chunkIndex: item.request.chunkIndex,
      error: errorMessage,
      attempt: nextAttempt,
      willRetry,
    });

    SentryService.addBreadcrumb({
      category: 'upload.service',
      message: `Chunk ${item.request.chunkId} failed (attempt ${nextAttempt}/${effectiveMaxRetries}): ${errorMessage}`,
      level: willRetry ? 'warning' : 'error',
    });

    if (willRetry) {
      // Schedule retry with exponential backoff + full jitter
      const delay = this.computeBackoff(nextAttempt);
      item.attempt = nextAttempt;
      item.abortController = new AbortController();

      SentryService.addBreadcrumb({
        category: 'upload.service',
        message: `Scheduling retry for chunk ${item.request.chunkId} in ${delay}ms`,
        level: 'info',
      });

      setTimeout(() => {
        if (this.disposed) return;
        // Re-enqueue at front (retries get priority)
        this.queue.unshift(item);
        this.updateState({ queueDepth: this.queue.length });
        this.processQueue();
      }, delay);
    } else {
      // Dead letter
      await this.deadLetterChunk(item, errorMessage);
      this.processQueue();
    }
  }

  /** Full jitter backoff: random() * min(cap, base * 2^attempt) */
  private computeBackoff(attempt: number): number {
    const exponential = this.retryBaseMs * Math.pow(2, attempt);
    const capped = Math.min(this.retryCapMs, exponential);
    return Math.random() * capped;
  }

  private async deadLetterChunk(item: QueueItem, error: string): Promise<void> {
    const entry: DeadLetterEntry = {
      sessionId: item.request.sessionId,
      chunkIndex: item.request.chunkIndex,
      chunkId: item.request.chunkId,
      contentType: item.request.contentType,
      byteSize: item.request.byteSize,
      error,
      attempts: item.attempt + 1,
      lastAttemptAt: Date.now(),
      createdAt: item.addedAt,
    };

    const updated = [...this.state.deadLetterQueue, entry];
    this.updateState({
      deadLetterQueue: updated,
      failedCount: updated.length,
    });

    // Persist to StorageService
    await this.persistDeadLetterQueue(updated);

    this.emit('chunkDeadLettered', { entry });

    SentryService.captureException(
      new Error(`Chunk dead-lettered: ${item.request.chunkId} (${error})`),
    );
  }

  // ── Dead Letter Persistence ──

  private async persistDeadLetterQueue(entries: DeadLetterEntry[]): Promise<void> {
    try {
      const data = new TextEncoder().encode(JSON.stringify(entries)).buffer as ArrayBuffer;
      await this.storageService.writeChunk(DEAD_LETTER_SESSION_KEY, 0, data);
    } catch (err) {
      SentryService.addBreadcrumb({
        category: 'upload.service',
        message: `Failed to persist dead letter queue: ${err}`,
        level: 'error',
      });
    }
  }

  private async loadDeadLetterQueue(): Promise<void> {
    try {
      const data = await this.storageService.readChunk(DEAD_LETTER_SESSION_KEY, 0);
      if (!data) return;

      const json = new TextDecoder().decode(data);
      const entries = JSON.parse(json) as DeadLetterEntry[];
      this.updateState({
        deadLetterQueue: entries,
        failedCount: entries.length,
      });
    } catch {
      // No persisted dead letter queue or corrupted data — start fresh
    }
  }

  // ── Drain Helpers ──

  /** Force-complete the drain by dead-lettering all remaining items. */
  private forceDrainAll(): void {
    SentryService.addBreadcrumb({
      category: 'upload.service',
      message: `Drain timeout — force-dead-lettering ${this.queue.length} queued + ${this.inFlight.size} in-flight`,
      level: 'warning',
    });

    // Abort all in-flight
    for (const item of this.inFlight.values()) {
      item.abortController.abort();
    }

    // Dead-letter everything remaining in the queue (synchronously move to dead letter)
    const remaining = [...this.queue];
    this.queue.length = 0;

    for (const item of remaining) {
      void this.deadLetterChunk(item, 'Drain timeout — force dead-lettered');
    }

    this.updateState({
      queueDepth: 0,
      // inFlight will be cleaned up by their catch blocks
    });

    // Resolve the drain promise
    this.resolveDrain(true);
  }

  /** Resolve the pending drain() promise and clean up drain state. */
  private resolveDrain(timedOut: boolean): void {
    if (!this.drainResolve) return;

    if (this.drainTimeoutId) {
      clearTimeout(this.drainTimeoutId);
      this.drainTimeoutId = null;
    }

    const result: DrainResult = {
      completedCount: this.state.completedCount - this.drainCompletedAtStart,
      failedCount: this.state.failedCount - this.drainFailedAtStart,
      timedOut,
    };

    const resolve = this.drainResolve;
    this.drainResolve = null;
    this.updateState({ isDraining: false });

    SentryService.addBreadcrumb({
      category: 'upload.service',
      message: `Drain complete (completed=${result.completedCount}, failed=${result.failedCount}, timedOut=${timedOut})`,
      level: 'info',
    });

    resolve(result);
  }

  // ── State Helpers ──

  private setServiceStatus(status: UploadServiceStatus): void {
    const prev = this.state.status;
    if (prev === status) return;
    this.updateState({ status });
    this.emit('statusChanged', { status, prev });
  }

  private updateState(partial: Partial<UploadState>): void {
    this.setState({ ...this.state, ...partial });
  }
}

// ── Error Types ──

export class UploadError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly retryable: boolean,
  ) {
    super(message);
    this.name = 'UploadError';
  }
}
